import { Button } from "@heroui/react";
import { Home, Menu } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import logo from "@/assets/images/bomiel.png";
import VersionLabel from "@/components/VersionLabel";
import { logout } from "@/lib/auth";
import { useAuth } from "@/lib/auth-context";
import { showError } from "@/lib/toast";

/**
 * アプリ共通のヘッダ。
 *
 * ngtg_msc の BomielAppBar に準拠し、左からメニューボタン・ロゴ・バージョン・
 * ホームボタン、右端にユーザー名とログアウトを置く。ページ名は表示しない。
 *
 * HeroUI の Navbar は使わず素の header にしている。Navbar は自前のテーマで
 * 背景色（bg-background/70 + backdrop-blur）を当てており、ブランドカラーの
 * 指定が打ち消されるため。sticky などの機能もこの画面では不要。
 */
export default function AppHeader({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  const { user, clear } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // サーバ側の失敗に関わらずローカルはログアウト扱いにする（Cookie が
      // 残っていても次の API 呼び出しで 401 になり、結局ログインへ誘導される）。
      showError("ログアウト処理でエラーが発生しましたが、ログアウトしました。");
    } finally {
      clear();
      navigate("/login", { replace: true });
    }
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 bg-brand-500 px-3 text-white shadow-md">
      <button
        type="button"
        aria-label="メニュー"
        onClick={onOpenDrawer}
        className="rounded-md p-1.5 transition-colors hover:bg-white/20"
      >
        <Menu size={28} />
      </button>

      {/* 青いロゴを白くして、ブランドカラーのヘッダ上でも見えるようにする。 */}
      <img src={logo} alt="BOMIEL" className="h-7 w-auto brightness-0 invert" />

      <VersionLabel className="text-xs font-bold" />

      {!isHome && (
        <button
          type="button"
          aria-label="ホーム"
          onClick={() => navigate("/")}
          className="rounded-md p-1.5 transition-colors hover:bg-white/20"
        >
          <Home size={24} />
        </button>
      )}

      <div className="ml-auto flex items-center gap-4">
        <span className="hidden text-sm sm:inline">{user?.name ?? user?.email}</span>
        <Button
          radius="full"
          onPress={() => void handleLogout()}
          className="bg-white font-medium text-brand-500 shadow-sm"
        >
          ログアウト
        </Button>
      </div>
    </header>
  );
}
